import { useState, useEffect } from 'react';
import { useAuthContext } from './AuthProvider';
import { getReferralCode, sendReferralInvite } from '../lib/api';
import posthog from '../lib/posthog';
import './ReferralSharePrompt.css';

/**
 * Post-first-project share prompt.
 *
 * Shown once after the user's first completed project to encourage sharing.
 * Call `ReferralSharePrompt.trigger()` to show it (if conditions are met).
 */

let _triggerShow = null;

export function triggerReferralSharePrompt() {
  if (_triggerShow) _triggerShow();
}

export default function ReferralSharePrompt() {
  const { user } = useAuthContext();
  const [visible, setVisible] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    _triggerShow = () => {
      if (!user) return;
      if (localStorage.getItem('latted_share_prompt_shown')) return;
      setVisible(true);
      localStorage.setItem('latted_share_prompt_shown', 'true');
    };
    return () => { _triggerShow = null; };
  }, [user]);

  useEffect(() => {
    if (!visible || !user) return;
    getReferralCode()
      .then(setReferralData)
      .catch(() => {});
  }, [visible, user]);

  const handleCopy = async () => {
    if (!referralData?.url) return;
    try {
      await navigator.clipboard.writeText(referralData.url);
      posthog.capture('referral link copied');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setSending(true);
    setMessage(null);
    try {
      await sendReferralInvite(trimmed);
      posthog.capture('referral invite sent');
      setEmail('');
      setMessage({ type: 'success', text: 'Invite sent!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to send.' });
    } finally {
      setSending(false);
    }
  };

  const handleDismiss = () => setVisible(false);

  if (!visible || !referralData) return null;

  return (
    <div className="share-prompt-backdrop" role="presentation" onClick={handleDismiss}>
      <div
        className="share-prompt-card"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="share-prompt-header">
          <h3>Share Latted, get $10 free</h3>
          <p>Share your link with a friend. When they sign up and buy $10+ in credits, you both earn $10.</p>
        </div>

        <div className="share-prompt-link-box">
          <input
            type="text"
            value={referralData.url}
            readOnly
            className="share-prompt-link-input"
            onClick={(e) => e.target.select()}
          />
          <button type="button" className="share-prompt-copy-btn" onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>

        <form className="share-prompt-email-form" onSubmit={handleSendInvite}>
          <input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (message) setMessage(null); }}
            placeholder="Friend's email"
            disabled={sending}
          />
          <button type="submit" disabled={sending || !email.trim()}>
            {sending ? 'Sending...' : 'Send invite'}
          </button>
        </form>

        {message && (
          <div className={`share-prompt-message share-prompt-message--${message.type}`}>
            {message.text}
          </div>
        )}

        <button type="button" className="share-prompt-dismiss" onClick={handleDismiss}>
          Maybe later
        </button>
      </div>
    </div>
  );
}
